import { Car } from "./types/CarTypes";
import { allCars } from './data';

import { Game, NUMBER_OF_ROUNDS, Outcome, OutcomeArray, Status } from "./types/GameTypes";

export const determineGameNumber = (today: Date): [number, number] => {
  const launchDay = new Date(Date.parse("2022-05-11")).setHours(0,0,0,0);
  // Don't be dumb in January (month 0)
  const dom = ((today.getMonth() + 1) * today.getDate() * today.getDate() * 8765);
  const dayOfYear = dom + today.getDate();
  const randIndex = (dayOfYear % allCars.length);
  const difference = today.getTime() - launchDay;
  const gameNumber = Math.ceil(difference / (1000 * 3600 * 24));
  return [randIndex, gameNumber];
}


export const initialiseGame = (): Game => {
  
  const outcomes = Array(NUMBER_OF_ROUNDS).fill(createEmptyOutcome()) as OutcomeArray
  
  return {
    guesses: [],
    outcomes,
    round: 0,
    gameResult: "NotStarted"
  }
}

export const createEmptyOutcome = ():Outcome => {
  return {
    madeIn: "Empty",
    manufacturer: "Empty",
    bodyStyle: "Empty",
    poweredBy: "Empty",
    model: "Empty"
  }
}

export const startGame = (oldGame: Game):Game => {
  const newGame = Object.assign({}, oldGame);
  newGame.gameResult = "InProgress"
  return newGame;
}

export const updateGame = (oldGame: Game, newRound: Outcome, guess: Car): Game => {
  const newGame = Object.assign({}, oldGame);
  newGame.outcomes[oldGame.round] = newRound;

  newGame.guesses[oldGame.round] = guess;

  newGame.round = oldGame.round + 1;

  const allCorrect = Object.values(newRound).every(outcome => outcome === "Right")

  if (allCorrect) {
    newGame.gameResult = "Won"
  } else {
    if (newGame.round === newGame.outcomes.length) {
      newGame.gameResult = "Lost";
      newGame.round = newGame.outcomes.length + 1; // Indicate the loss
    }
  }
  return newGame;
}

export const evaluateGuess = (targetCar: Car, car: Car):Outcome => {
  return {
    madeIn: evaluateMadeIn(targetCar, car),
    manufacturer: evaluateManufacturer(targetCar, car),
    bodyStyle: evaluateBodyStyle(targetCar, car),
    poweredBy: evaluatePoweredBy(targetCar, car),
    model: evaluateModel(targetCar, car),
  }
}

const evaluateMadeIn = (targetCar: Car, car: Car):Status => {
  if (targetCar.manufacturer.country === car.manufacturer.country) {
    return "Right"
  }
  if (targetCar.manufacturer.country.continent === car.manufacturer.country.continent) {
    return "Close"
  }
  return "Wrong"
}
const evaluateManufacturer = (targetCar: Car, car: Car):Status => {
  if (targetCar.manufacturer.name === car.manufacturer.name) {
    return "Right"
  }
  if (targetCar.manufacturer.parent && targetCar.manufacturer.parent === car.manufacturer.parent) {
    return "Close"
  }
  return "Wrong"
}
const evaluateBodyStyle = (targetCar: Car, car: Car):Status => {
  if (targetCar.bodyStyle.name === car.bodyStyle.name) {
    return "Right"
  }

  if (targetCar.bodyStyle.family === car.bodyStyle.family) {
    return "Close"
  }
  return "Wrong"
}
const evaluatePoweredBy = (targetCar: Car, car: Car):Status => {
  if (targetCar.poweredBy === car.poweredBy) {
    return "Right"
  }

  // DIESEL is adjacent to ICE
  // ICE is adjacent to DIESEL and HYBRID
  // HYBRID is adjacent to ICE and EV
  // EV is adjacent to HYBRID 
  switch (targetCar.poweredBy) {
    case "Diesel": 
      return car.poweredBy === "ICE" ? "Close" : "Wrong";
    case "EV": 
      return car.poweredBy === "Hybrid" ? "Close" : "Wrong";
    case "ICE": 
      return (car.poweredBy === "Hybrid" || car.poweredBy === "Diesel") ? "Close" : "Wrong";
    case "Hybrid": 
    default:
      return (car.poweredBy === "ICE" || car.poweredBy === "EV") ? "Close" : "Wrong";

  }

}
const evaluateModel = (targetCar: Car, car: Car):Status => {
  if (targetCar.model === car.model) {
    return "Right"
  }
  
  const found = targetCar.model.split('').find ( targetCarLetter => {
    return car.model.includes(targetCarLetter);
  })


  return found ? "Close" : "Wrong"
}

export const renderGameToString = (game: Game, gameNumber: number):string => {
  const result = game.round > 5 ? "LOST 😫" : `${game.round}/5 🎉`;
  const header = `Cardle ${gameNumber} ${result}`;
  const gameRows = game.outcomes.map(outcomeRow => {
    return `${toEmoji(outcomeRow.manufacturer)}${toEmoji(outcomeRow.model)}${toEmoji(outcomeRow.bodyStyle)}${toEmoji(outcomeRow.madeIn)}${toEmoji(outcomeRow.poweredBy)}`;
  }).filter(emojiRow => emojiRow.trim().length > 0);
  return [header, "", ...gameRows,""].join("\n");
}

const toEmoji = (status: Status):string => {
  switch(status) {
    case "Wrong": return "⬛";
    case "Close": return "🟨";
    case "Right": return "🟩";
    case "Empty": return "";
  }
}